import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { listingImageUrl } from "@/lib/productGallery";

const CartCtx = createContext(null);

function key(slug) { return `cart:${slug}`; }

function matchLine(i, product_id, variant_id, line_id) {
  if (line_id) return i.line_id === line_id;
  return i.product_id === product_id && (i.variant_id || null) === (variant_id || null) && !i.freeze_qty;
}

export function CartProvider({ slug, children }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key(slug));
      setItems(raw ? JSON.parse(raw) : []);
    } catch {
      setItems([]);
    }
    setLoaded(true);
  }, [slug]);

  const save = useCallback((next) => {
    setItems(next);
    localStorage.setItem(key(slug), JSON.stringify(next));
  }, [slug]);

  const add = useCallback((product, qty = 1, variant = null, extra = null) => {
    const form = extra?.custom_form;
    const print = extra?.custom_print;
    const freeze = Boolean(form || print);
    const price = extra?.unit_price ?? (variant?.price || product.price);
    const mrp = variant?.mrp || product.mrp;
    const image = print?.preview_url || variant?.image_url || listingImageUrl(product.images);
    const symbol = product.currency_symbol || extra?.currency_symbol || "₹";
    if (freeze) {
      const line = {
        line_id: `ln_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        product_id: product.id,
        variant_id: variant?.id || null,
        variant_name: variant?.name || null,
        name: variant?.name ? `${product.name} — ${variant.name}` : product.name,
        slug: product.slug,
        image,
        price,
        mrp,
        qty: extra?.qty || 1,
        freeze_qty: true,
        custom_form: form,
        custom_form_snapshot: extra?.snapshot || [],
        custom_print: print || null,
        currency_symbol: symbol,
        currency: product.currency || extra?.currency,
        shipping_price: product.shipping_price == null ? null : Number(product.shipping_price),
      };
      save([...items, line]);
      return;
    }
    const key_ = (product.id) + (variant?.id ? `::${variant.id}` : "");
    const idx = items.findIndex((i) => !i.freeze_qty && ((i.product_id) + (i.variant_id ? `::${i.variant_id}` : "")) === key_);
    let next;
    if (idx >= 0) {
      next = items.map((i, k) => (k === idx ? { ...i, qty: i.qty + qty, shipping_price: product.shipping_price == null ? i.shipping_price : Number(product.shipping_price) } : i));
    } else {
      next = [
        ...items,
        {
          product_id: product.id,
          variant_id: variant?.id || null,
          variant_name: variant?.name || null,
          name: variant?.name ? `${product.name} — ${variant.name}` : product.name,
          slug: product.slug,
          image,
          price,
          mrp,
          qty,
          currency_symbol: symbol,
          currency: product.currency,
          shipping_price: product.shipping_price == null ? null : Number(product.shipping_price),
        },
      ];
    }
    save(next);
  }, [items, save]);

  const setQty = useCallback((product_id, variant_id, qty, line_id) => {
    const match = (i) => matchLine(i, product_id, variant_id, line_id);
    if (qty <= 0) {
      save(items.filter((i) => !match(i)));
      return;
    }
    save(items.map((i) => {
      if (!match(i)) return i;
      if (i.freeze_qty) return i;
      return { ...i, qty };
    }));
  }, [items, save]);

  const remove = useCallback((product_id, variant_id, line_id) => {
    save(items.filter((i) => !matchLine(i, product_id, variant_id, line_id)));
  }, [items, save]);

  const clear = useCallback(() => save([]), [save]);

  const replaceWith = useCallback((product, qty = 1, variant = null) => {
    const price = variant?.price || product.price;
    const mrp = variant?.mrp || product.mrp;
    const image = variant?.image_url || listingImageUrl(product.images);
    const symbol = product.currency_symbol || "₹";
    save([
      {
        product_id: product.id,
        variant_id: variant?.id || null,
        variant_name: variant?.name || null,
        name: variant?.name ? `${product.name} — ${variant.name}` : product.name,
        slug: product.slug,
        image,
        price,
        mrp,
        qty,
        currency_symbol: symbol,
        currency: product.currency,
        shipping_price: product.shipping_price == null ? null : Number(product.shipping_price),
      },
    ]);
  }, [save]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.qty, 0),
    [items]
  );

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return (
    <CartCtx.Provider value={{ items, add, setQty, remove, clear, replaceWith, loaded, subtotal, count }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
